import { AppContainer, createSliceFoundation, EntityDescriptor, EntityEditorPage, EntityEditorPageProps, EntityTablePage, EntityTablePageProps, EntityTablePageReducers, EntityTablePageState, FieldDescriptor, SliceEntityEditorPage, Utils } from "@crispico/foundation-react";
import DateFieldRenderer from "@crispico/foundation-react/entity_crud/fieldRenderers/DateFieldRenderer";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import React from "react";
import { ID } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import { ReduxReusableComponents } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { EventGenerationSourceTypeEnum } from "apollo-gen/globalTypes";
import { MissionEvent_MessagesFieldRenderer, MissionEvent_ObservationFieldRenderer, ObjectIdFieldDescriptor } from "./MissionEvent2FieldDescriptors";
import { MissionStatus } from "pages/gantt/typeRenderers/constants";
import moment from "moment-timezone";
import { OVERDUE_MINUTES } from "actions/OpenClosedAndOverdueMissions";
import { TerritoriesFieldDescriptor } from "pages/EquipmentResource/EquipmentResourceUtils";

const FIELDS_FROM_DATA = ["observation", "statusChangedAt", "stopInfo", "flight", "objectActionInfo", "message", "driver", "statusChangedBy", "answer", "label", "comment", "type", "territories"];
const DISABLED_FIELDS = ["userId", "driverId", "generationSource"];
const additionalDateFieldRendererProps = FieldDescriptor.castAdditionalFieldRendererProps(DateFieldRenderer, { format: Utils.dateTimeFormatShorter, formatForToday: Utils.timeFormat });

const sliceMissionEvent2EditorPage = createSliceFoundation(class SliceMissionEvent2EditorPage extends SliceEntityEditorPage {
    isDefaultErrorHandlerShownInCaseOfValidationException(): boolean {
        return true;
    }
});

class MissionEvent2TablePage extends EntityTablePage<EntityTablePageProps> {
    protected getOneToManyModeAddParams() {
        if (!this.props.oneToManyMode) {
            return "";
        }
        const { field, entity } = this.props.oneToManyMode;
        // In MissionEvent2, the one-to-many fields were stored using only the ID.
        let result = "?" + field + "=" + entity?.[ID];
        if (field.endsWith("Id")) {
            // also add the entity to not load from DB
            result += "&" + field.slice(0, field.length - 2) + "=" + JSON.stringify(entity);
        }
        return result;
    }
}

const MissionEvent2TablePageRRC = ReduxReusableComponents.connectRRC(EntityTablePageState, EntityTablePageReducers, MissionEvent2TablePage);

export class MissionEvent2EntityDescriptor extends EntityDescriptor {

    protected customize() {
        this.isInDefaultColumnConfig(true, "creationdate", "descriptorType", "missionId", "oagId", "stopId", "objectId", "generationSource")
        this.addFieldDescriptor({ name: "creationdate", type: FieldType.date, additionalFieldRendererProps: additionalDateFieldRendererProps })
        this.addFieldDescriptor({ name: "statusChangedAt", type: FieldType.date, additionalFieldRendererProps: additionalDateFieldRendererProps })
        this.addFieldDescriptor(
            { name: "message", type: FieldType.string },
            new class extends FieldDescriptor {
                protected renderFieldInternal(RendererClass: any, props: any, entity: any) {
                    return React.createElement(MissionEvent_MessagesFieldRenderer, props);
                }
            }()
        )
        this.addFieldDescriptor({ name: "observation", type: FieldType.string },
            new class extends FieldDescriptor {
                protected renderFieldInternal(RendererClass: any, props: any, entity: any) {
                    return React.createElement(MissionEvent_ObservationFieldRenderer, props);
                }
            }()
        )
        this.addFieldDescriptor({ name: "objectId" }, new ObjectIdFieldDescriptor());
        this.addFieldDescriptor({ name: "territories", enabled: false }, new TerritoriesFieldDescriptor("territories"));

        this.doForFields(FIELDS_FROM_DATA, fd => fd.filterable = false);
        this.doForFields(FIELDS_FROM_DATA, fd => fd.sortable = false);
        this.doForFields(DISABLED_FIELDS, fd => fd.enabled = false);
        this.infoEditor.wrappedComponentClass = class extends EntityEditorPage<EntityEditorPageProps> {

            protected async onSaveInternal() {
                let newEntity = {
                    creationdate: Utils.now().toISOString(),
                    userId: AppContainer.INSTANCE.getAppContainerContextValue().initializationsForClient.currentUser?.id,
                    ...this.props.entity
                };
                const { mission } = this.props.entity;
                if (mission && (mission.status == MissionStatus.MISSION_STATUS_FINISHED || moment(mission.endTime).valueOf() < moment().add(-OVERDUE_MINUTES, 'minutes').valueOf())) {
                    newEntity["generationSource"] = EventGenerationSourceTypeEnum.RECONCILIATION;
                }
                await this.props.dispatchers.save(newEntity, undefined, undefined, { componentProps: this.props });
            }
        }
        this.infoEditor.slice = sliceMissionEvent2EditorPage;
        sliceMissionEvent2EditorPage.setEntityDescriptor(this);
    }

    renderTable() {
        return <MissionEvent2TablePageRRC {...super.renderTable().props} ref={this.entityTablePage} />;
    }
}
